import { computed, ref } from 'vue'

import { defineStore } from 'pinia'

import { CourseSortBy, SortDirection, TAG_IDS_FILTER_MAX_SIZE } from '../constants/courses-filtering'
import { sanitizeCourseTagIds } from '../utils/courses-filtering'

import type { CourseSortByValue, ICourseProduct, SortDirectionValue } from '../types'

export const useLearningUIStore = defineStore('learning-ui', () => {
  // ===== DRAG & DROP STATE =====
  const isDragActive = ref(false)
  const activeDragIndex = ref<number | null>(null)

  // ===== MODAL STATE =====
  const showCourseDetails = ref(false)
  const selectedCourse = ref<ICourseProduct | null>(null)
  const showAddCourseModal = ref(false)
  const showDeleteConfirmation = ref(false)
  const showEditCourseModal = ref(false)

  // ===== COURSE SELECTION STATE =====
  const selectedCourseIds = ref<Set<string>>(new Set())

  // ===== VIEW STATE =====
  const viewMode = ref<'grid' | 'list'>('grid')
  const sortOrder = ref<'newest' | 'oldest' | 'alphabetical'>('newest')

  // ===== FILTER STATE =====
  const searchQuery = ref('')
  const selectedMembershipType = ref<string>('all')
  const selectedProgress = ref<string>('all')

  // ===== TAG FILTER + SERVER SORT STATE =====
  // These drive the v2 `/user/courses` endpoint filters. Selected tag ids are the exact ObjectId
  // strings the backend expects (`@IsMongoId`), kept as a plain array (not a Set) so exposing
  // them as a computed ref does not lose reactivity and downstream consumers can pass them
  // directly into `queryKey` without a manual `[...set]` conversion on every change.
  const selectedTagIds = ref<string[]>([])
  const serverSortBy = ref<CourseSortByValue>(CourseSortBy.SEQUENCE)
  const serverSort = ref<SortDirectionValue>(SortDirection.DESCENDING)

  // ===== COMPUTED PROPERTIES =====
  const isAnyModalOpen = computed(() => {
    return showCourseDetails.value || showAddCourseModal.value || showDeleteConfirmation.value || showEditCourseModal.value
  })

  const hasSelectedCourses = computed(() => {
    return selectedCourseIds.value.size > 0
  })

  const hasActiveTagFilters = computed(() => selectedTagIds.value.length > 0)

  const hasActiveServerSort = computed(() => serverSortBy.value !== CourseSortBy.SEQUENCE || serverSort.value !== SortDirection.DESCENDING)

  // ===== DRAG & DROP ACTIONS =====
  const setDragActive = (active: boolean) => {
    isDragActive.value = active
  }

  const setActiveDragIndex = (index: number | null) => {
    activeDragIndex.value = index
  }

  const startDrag = (index: number) => {
    isDragActive.value = true
    activeDragIndex.value = index
  }

  const endDrag = () => {
    isDragActive.value = false
    activeDragIndex.value = null
  }

  // ===== MODAL ACTIONS =====
  const openCourseDetails = (course: ICourseProduct) => {
    selectedCourse.value = course
    showCourseDetails.value = true
  }

  const closeCourseDetails = () => {
    showCourseDetails.value = false
    selectedCourse.value = null
  }

  const openAddCourseModal = () => {
    showAddCourseModal.value = true
  }

  const closeAddCourseModal = () => {
    showAddCourseModal.value = false
  }

  const openEditCourseModal = (course: ICourseProduct) => {
    selectedCourse.value = course
    showEditCourseModal.value = true
  }

  const closeEditCourseModal = () => {
    showEditCourseModal.value = false
    selectedCourse.value = null
  }

  const openDeleteConfirmation = (course: ICourseProduct) => {
    selectedCourse.value = course
    showDeleteConfirmation.value = true
  }

  const closeDeleteConfirmation = () => {
    showDeleteConfirmation.value = false
    selectedCourse.value = null
  }

  const closeAllModals = () => {
    showCourseDetails.value = false
    showAddCourseModal.value = false
    showDeleteConfirmation.value = false
    showEditCourseModal.value = false
    selectedCourse.value = null
  }

  // ===== COURSE SELECTION ACTIONS =====
  const selectCourse = (courseId: string) => {
    selectedCourseIds.value.add(courseId)
  }

  const deselectCourse = (courseId: string) => {
    selectedCourseIds.value.delete(courseId)
  }

  const toggleCourseSelection = (courseId: string) => {
    if (selectedCourseIds.value.has(courseId)) {
      deselectCourse(courseId)
    } else {
      selectCourse(courseId)
    }
  }

  const clearSelection = () => {
    selectedCourseIds.value.clear()
  }

  const selectAllCourses = (courseIds: string[]) => {
    selectedCourseIds.value = new Set(courseIds)
  }

  // ===== VIEW ACTIONS =====
  const setViewMode = (mode: 'grid' | 'list') => {
    viewMode.value = mode
  }

  const setSortOrder = (order: 'newest' | 'oldest' | 'alphabetical') => {
    sortOrder.value = order
  }

  // ===== FILTER ACTIONS =====
  const setSearchQuery = (query: string) => {
    searchQuery.value = query
  }

  const setSelectedMembershipType = (type: string) => {
    selectedMembershipType.value = type
  }

  const setSelectedProgress = (progress: string) => {
    selectedProgress.value = progress
  }

  const clearFilters = () => {
    searchQuery.value = ''
    selectedMembershipType.value = 'all'
    selectedProgress.value = 'all'
  }

  // ===== TAG FILTER + SERVER SORT ACTIONS =====

  const setSelectedTagIds = (ids: readonly string[]) => {
    // Sanitize at the store boundary so invalid ids never poison the query key or the API request.
    // Cap enforcement (TAG_IDS_FILTER_MAX_SIZE) happens visibly in the filter panel — this
    // sanitizer only exists as a safety net for programmatic or hydrated callers.
    selectedTagIds.value = sanitizeCourseTagIds(ids)
  }

  /**
   * Additive multi-select toggle: picking an unchecked tag appends it, picking a checked tag
   * removes it. Selections are category-agnostic — the store treats `selectedTagIds` as an
   * opaque bag of ids and doesn't know which category any id belongs to. Ordering follows
   * click order; the query-store re-sorts before it hits the queryKey so cache identity is
   * insensitive to toggle order.
   */
  const toggleTagId = (id: string) => {
    const current = selectedTagIds.value
    if (current.includes(id)) {
      selectedTagIds.value = current.filter(existing => existing !== id)
      return
    }
    // Defense-in-depth: CoursesFilterSidebar disables unchecked options at the cap, so this
    // early return should not fire from a real user click. Kept for programmatic toggles /
    // hydrated state that might overflow the backend's `@ArrayMaxSize(50)` limit.
    if (current.length >= TAG_IDS_FILTER_MAX_SIZE) return
    selectedTagIds.value = sanitizeCourseTagIds([...current, id])
  }

  const clearSelectedTagIds = () => {
    selectedTagIds.value = []
  }

  const setServerSort = (sortBy: CourseSortByValue, sort: SortDirectionValue) => {
    serverSortBy.value = sortBy
    serverSort.value = sort
  }

  const resetServerSort = () => {
    serverSortBy.value = CourseSortBy.SEQUENCE
    serverSort.value = SortDirection.DESCENDING
  }

  // ===== RESET =====
  const reset = () => {
    isDragActive.value = false
    activeDragIndex.value = null
    showCourseDetails.value = false
    selectedCourse.value = null
    showAddCourseModal.value = false
    showDeleteConfirmation.value = false
    showEditCourseModal.value = false
    selectedCourseIds.value.clear()
    viewMode.value = 'grid'
    sortOrder.value = 'newest'
    clearFilters()
    clearSelectedTagIds()
    resetServerSort()
  }

  return {
    // State
    isDragActive: computed(() => isDragActive.value),
    activeDragIndex: computed(() => activeDragIndex.value),
    showCourseDetails: computed(() => showCourseDetails.value),
    selectedCourse: computed(() => selectedCourse.value),
    showAddCourseModal: computed(() => showAddCourseModal.value),
    showDeleteConfirmation: computed(() => showDeleteConfirmation.value),
    showEditCourseModal: computed(() => showEditCourseModal.value),
    selectedCourseIds: computed(() => selectedCourseIds.value),
    viewMode: computed(() => viewMode.value),
    sortOrder: computed(() => sortOrder.value),
    searchQuery: computed(() => searchQuery.value),
    selectedMembershipType: computed(() => selectedMembershipType.value),
    selectedProgress: computed(() => selectedProgress.value),
    selectedTagIds: computed(() => selectedTagIds.value),
    serverSortBy: computed(() => serverSortBy.value),
    serverSort: computed(() => serverSort.value),

    // Computed
    isAnyModalOpen,
    hasSelectedCourses,
    hasActiveTagFilters,
    hasActiveServerSort,

    // Actions
    setDragActive,
    setActiveDragIndex,
    startDrag,
    endDrag,
    openCourseDetails,
    closeCourseDetails,
    openAddCourseModal,
    closeAddCourseModal,
    openEditCourseModal,
    closeEditCourseModal,
    openDeleteConfirmation,
    closeDeleteConfirmation,
    closeAllModals,
    selectCourse,
    deselectCourse,
    toggleCourseSelection,
    clearSelection,
    selectAllCourses,
    setViewMode,
    setSortOrder,
    setSearchQuery,
    setSelectedMembershipType,
    setSelectedProgress,
    clearFilters,
    setSelectedTagIds,
    toggleTagId,
    clearSelectedTagIds,
    setServerSort,
    resetServerSort,
    reset,
  }
})
